'use client';

import { useState } from 'react';
import { Clipboard, Check, Book, Link as LinkIcon, User, Calendar, Type, BookOpen, Globe, Database, FileText, Info } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { citationGeneratorContent } from '../../data/citation-generator-content';

type CitationStyle = 'apa' | 'mla' | 'chicago';
type SourceType = 'book' | 'website' | 'article' | 'journal';

interface CitationFormData {
    author: string;
    title: string;
    year: string;
    publisher?: string;
    url?: string;
    websiteTitle?: string;
    journal?: string;
    volume?: string;
    issue?: string;
    pages?: string;
    doi?: string;
}

export default function CitationGenerator() {
    const [activeTab, setActiveTab] = useState<CitationStyle>('apa');
    const [sourceType, setSourceType] = useState<SourceType>('book');
    const [copied, setCopied] = useState(false);
    const [formData, setFormData] = useState<CitationFormData>({
        author: '',
        title: '',
        year: new Date().getFullYear().toString(),
        publisher: '',
        url: '',
        websiteTitle: '',
        journal: '',
        volume: '',
        issue: '',
        pages: '',
        doi: ''
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const generateCitation = (): string => {
        const {
            author,
            title,
            year,
            publisher,
            url,
            websiteTitle,
            volume,
            issue,
            pages,
            doi
        } = formData;

        // Common citation parts
        const authorPart = author ? `${author} ` : '';
        const yearPart = year ? `(${year}). ` : '';
        const titlePart = title ? `${title}. ` : '';

        switch (activeTab) {
            case 'apa':
                if (sourceType === 'journal') {
                    // APA Journal Article format: Author, A. A. (Year). Title of article. Title of Journal, volume(issue), pages. https://doi.org/xx.xxx/yyyy
                    return `${authorPart}${yearPart}${titlePart}${publisher || 'Journal Name'}, ${volume || 'XX'}${issue ? `(${issue})` : ''}${pages ? `, ${pages}` : ''}.${doi ? ` https://doi.org/${doi}` : ''}`;
                } else if (sourceType === 'book') {
                    // APA Book format: Author, A. A. (Year). Title of work. Publisher.
                    return `${authorPart}${yearPart}${titlePart}${publisher || 'Publisher'}.`;
                } else if (sourceType === 'website') {
                    // APA Website format: Author, A. A. (Year, Month Day). Title of page. Website Name. URL
                    return `${authorPart}(${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}). ${titlePart}${websiteTitle || 'Website'}. ${url || ''}`;
                } else {
                    // Default APA format
                    return `${authorPart}${yearPart}${titlePart}${publisher ? `${publisher}. ` : ''}${url || ''}`;
                }

            case 'mla':
                if (sourceType === 'journal') {
                    // MLA Journal Article format: Author(s). "Title of Article." Title of Journal, vol. number, no. issue, Year, pages.
                    return `${authorPart}"${titlePart}" ${publisher || 'Journal Name'}, vol. ${volume || 'X'}, no. ${issue || 'X'}, ${year}${pages ? `, pp. ${pages}` : ''}.`;
                } else if (sourceType === 'book') {
                    // MLA Book format: Author(s). Title of Book. Publisher, Year.
                    return `${authorPart}${titlePart}${publisher || 'Publisher'}, ${year}.`;
                } else if (sourceType === 'website') {
                    // MLA Website format: Author(s). "Title of Web Page." Website Name, Publisher, Date, URL.
                    return `${authorPart}"${titlePart}" ${websiteTitle || 'Website'}, ${publisher || 'Publisher'}, ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}, ${url || ''}.`;
                } else {
                    return `${authorPart}"${titlePart}" ${publisher ? `${publisher}, ` : ''}${year}.${url ? ` ${url}.` : ''}`;
                }

            case 'chicago':
                if (sourceType === 'journal') {
                    // Chicago Journal Article format: Author, A. "Title of Article." Journal Name volume, no. issue (Year): pages.
                    return `${authorPart}"${titlePart}" ${publisher || 'Journal Name'} ${volume || 'X'}, no. ${issue || 'X'} (${year})${pages ? `: ${pages}` : ''}.`;
                } else if (sourceType === 'book') {
                    // Chicago Book format: Author, A. Title of Book. Place of publication: Publisher, Year.
                    return `${authorPart}${titlePart}${publisher ? `${publisher}, ` : ''}${year}.`;
                } else if (sourceType === 'website') {
                    // Chicago Website format: Author, A. "Title of Web Page." Website Name. Month Day, Year. URL.
                    return `${authorPart}"${titlePart}" ${websiteTitle || 'Website'}. ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}. ${url || ''}.`;
                } else {
                    return `${authorPart}${yearPart}"${titlePart}" ${publisher ? `${publisher}. ` : ''}${url ? `\n${url}` : ''}`;
                }
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(generateCitation());
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
                <ToolPageIntro title="Citation Generator">
                Generate citations in APA, MLA, Chicago, and Harvard styles with our free online tool. Create properly formatted references for academic papers.
            </ToolPageIntro>

                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    {/* Style Tabs */}
                    <div className="flex border-b mb-6">
                        {['apa', 'mla', 'chicago'].map((style) => (
                            <button
                                key={style}
                                onClick={() => setActiveTab(style as CitationStyle)}
                                className={`px-4 py-2 font-medium ${activeTab === style
                                    ? 'border-b-2 border-blue-600 text-blue-600'
                                    : 'text-gray-600 hover:text-gray-800'
                                    }`}
                            >
                                {style.toUpperCase()}
                            </button>
                        ))}
                    </div>

                    {/* Source Type Selector */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Source Type</label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {[
                                { value: 'book', label: 'Book', icon: <Book className="w-4 h-4" /> },
                                { value: 'website', label: 'Website', icon: <Globe className="w-4 h-4" /> },
                                { value: 'article', label: 'Article', icon: <FileText className="w-4 h-4" /> },
                                { value: 'journal', label: 'Journal', icon: <BookOpen className="w-4 h-4" /> },
                            ].map((type) => (
                                <button
                                    key={type.value}
                                    onClick={() => setSourceType(type.value as SourceType)}
                                    className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md ${sourceType === type.value
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    {type.icon}
                                    {type.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-1">
                                Author(s)
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    id="author"
                                    name="author"
                                    value={formData.author}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Smith, J. or Smith, John"
                                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                                Title
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Type className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    placeholder="Title of the source"
                                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1">
                                    Year
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Calendar className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        id="year"
                                        name="year"
                                        value={formData.year}
                                        onChange={handleInputChange}
                                        placeholder="e.g., 2023"
                                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            {sourceType === 'book' || sourceType === 'journal' ? (
                                <div>
                                    <label htmlFor="publisher" className="block text-sm font-medium text-gray-700 mb-1">
                                        {sourceType === 'book' ? 'Publisher' : 'Journal Name'}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Database className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            id="publisher"
                                            name="publisher"
                                            value={formData.publisher}
                                            onChange={handleInputChange}
                                            placeholder={sourceType === 'book' ? 'Publisher name' : 'Journal name'}
                                            className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        {(sourceType === 'website' || sourceType === 'article') && (
                            <div>
                                <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
                                    {sourceType === 'website' ? 'Website URL' : 'Article URL'}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <LinkIcon className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="url"
                                        id="url"
                                        name="url"
                                        value={formData.url}
                                        onChange={handleInputChange}
                                        placeholder="https://example.com"
                                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        )}

                        {sourceType === 'website' && (
                            <div>
                                <label htmlFor="websiteTitle" className="block text-sm font-medium text-gray-700 mb-1">
                                    Website Title
                                </label>
                                <input
                                    type="text"
                                    id="websiteTitle"
                                    name="websiteTitle"
                                    value={formData.websiteTitle}
                                    onChange={handleInputChange}
                                    placeholder="Name of the website"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                        )}

                        {sourceType === 'journal' && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label htmlFor="volume" className="block text-sm font-medium text-gray-700 mb-1">
                                        Volume
                                    </label>
                                    <input
                                        type="text"
                                        id="volume"
                                        name="volume"
                                        value={formData.volume}
                                        onChange={handleInputChange}
                                        placeholder="Volume number"
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="issue" className="block text-sm font-medium text-gray-700 mb-1">
                                        Issue
                                    </label>
                                    <input
                                        type="text"
                                        id="issue"
                                        name="issue"
                                        value={formData.issue}
                                        onChange={handleInputChange}
                                        placeholder="Issue number"
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="pages" className="block text-sm font-medium text-gray-700 mb-1">
                                        Pages
                                    </label>
                                    <input
                                        type="text"
                                        id="pages"
                                        name="pages"
                                        value={formData.pages}
                                        onChange={handleInputChange}
                                        placeholder="e.g., 45-67"
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Citation Output */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">Your Citation</h2>
                        <button
                            onClick={copyToClipboard}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            {copied ? (
                                <>
                                    <Check className="h-4 w-4 mr-2" />
                                    Copied!
                                </>
                            ) : (
                                <>
                                    <Clipboard className="h-4 w-4 mr-2" />
                                    Copy Citation
                                </>
                            )}
                        </button>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-md">
                        <pre className="whitespace-pre-wrap font-sans text-gray-800">
                            {generateCitation()}
                        </pre>
                    </div>

                    <div className="mt-4 text-sm text-gray-500">
                        <p>Format: {activeTab.toUpperCase()}</p>
                        <p>Source: {sourceType.charAt(0).toUpperCase() + sourceType.slice(1)}</p>
                    </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <Info className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">Citation Tips</h3>
                            <div className="mt-2 text-sm text-blue-700">
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Make sure to include all required fields for your citation style</li>
                                    <li>For multiple authors, separate names with commas</li>
                                    <li>Double-check the formatting against the official style guide</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SEO CONTENT */}
                <div className="mt-16">
                    <InformationalContent
                        title={citationGeneratorContent.title}
                        description={citationGeneratorContent.description}
                        content={citationGeneratorContent.content}
                        faqs={citationGeneratorContent.faqs}
                        relatedTools={citationGeneratorContent.relatedTools}
                    />
                </div>
            </div>
        </div>
    );
}
